import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// Abmeldeseite für Recall-Mails (/recall/abmelden/:token?lang=de).
// Bewusst mit Bestätigungs-Button statt Auto-Widerruf beim Laden —
// Mail-Scanner klicken Links an und würden sonst ungewollt abmelden.

const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? 'https://admin.seadent.es/api'
    : 'http://localhost:5000/api';

type Lang = 'de' | 'en' | 'es' | 'ru';

const STRINGS: Record<Lang, {
    title: string; body: string; confirm: string; working: string;
    doneTitle: string; doneBody: string; alreadyTitle: string; errorLink: string;
}> = {
    de: {
        title: 'Termin-Empfehlungen abbestellen',
        body: 'Möchten Sie keine Termin-Empfehlungen (z. B. zur Zahnreinigung oder Kontrolle) mehr per E-Mail erhalten? Terminbestätigungen und -erinnerungen zu gebuchten Terminen erhalten Sie weiterhin.',
        confirm: 'Ja, abmelden',
        working: 'Wird abgemeldet …',
        doneTitle: 'Abgemeldet',
        doneBody: 'Sie erhalten keine Termin-Empfehlungen mehr per E-Mail. Sie können sich jederzeit in der Praxis wieder anmelden.',
        alreadyTitle: 'Bereits abgemeldet',
        errorLink: 'Dieser Link ist ungültig oder abgelaufen.',
    },
    en: {
        title: 'Unsubscribe from appointment recommendations',
        body: 'Do you no longer wish to receive appointment recommendations (e.g. for cleanings or check-ups) by email? You will still receive confirmations and reminders for booked appointments.',
        confirm: 'Yes, unsubscribe',
        working: 'Unsubscribing …',
        doneTitle: 'Unsubscribed',
        doneBody: 'You will no longer receive appointment recommendations by email. You can opt in again at the practice at any time.',
        alreadyTitle: 'Already unsubscribed',
        errorLink: 'This link is invalid or has expired.',
    },
    es: {
        title: 'Darse de baja de las recomendaciones de cita',
        body: '¿No desea recibir más recomendaciones de cita (p. ej., para limpiezas o revisiones) por correo electrónico? Seguirá recibiendo confirmaciones y recordatorios de las citas reservadas.',
        confirm: 'Sí, darme de baja',
        working: 'Procesando …',
        doneTitle: 'Baja realizada',
        doneBody: 'Ya no recibirá recomendaciones de cita por correo electrónico. Puede volver a suscribirse en la clínica en cualquier momento.',
        alreadyTitle: 'Ya estaba dado de baja',
        errorLink: 'Este enlace no es válido o ha caducado.',
    },
    ru: {
        title: 'Отписаться от рекомендаций о записи',
        body: 'Не хотите больше получать рекомендации о записи (например, на чистку или осмотр) по электронной почте? Подтверждения и напоминания о забронированных приёмах вы будете получать по-прежнему.',
        confirm: 'Да, отписаться',
        working: 'Отписываем …',
        doneTitle: 'Вы отписаны',
        doneBody: 'Вы больше не будете получать рекомендации о записи по электронной почте. Вы можете снова подписаться в клинике в любое время.',
        alreadyTitle: 'Вы уже отписаны',
        errorLink: 'Эта ссылка недействительна или устарела.',
    },
};

type View = 'loading' | 'confirm' | 'done' | 'already' | 'error';

const RecallUnsubscribePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const location = useLocation();
    const langParam = new URLSearchParams(location.search).get('lang') as Lang | null;
    const t = STRINGS[langParam && STRINGS[langParam] ? langParam : 'de'];

    const [view, setView] = useState<View>('loading');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) { setView('error'); return; }
        let cancelled = false;
        fetch(`${API_BASE_URL}/public/recall/unsubscribe/${encodeURIComponent(token)}`)
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(d => { if (!cancelled) setView(d.already_revoked ? 'already' : 'confirm'); })
            .catch(() => { if (!cancelled) setView('error'); });
        return () => { cancelled = true; };
    }, [token]);

    const doUnsubscribe = async () => {
        if (!token) return;
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE_URL}/public/recall/unsubscribe/${encodeURIComponent(token)}`, { method: 'POST' });
            setView(r.ok ? 'done' : 'error');
        } catch {
            setView('error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#f9f9f7] to-white px-6 py-10 text-center">
            <div className="max-w-md w-full">
                <div className="mb-6 text-[#384860] font-semibold tracking-wide">SeaDent Mallorca</div>

                {view === 'loading' && (
                    <div className="h-12 w-12 mx-auto rounded-full border-4 border-[#384860]/20 border-t-[#384860] animate-spin" />
                )}

                {view === 'error' && (
                    <h1 className="text-xl font-semibold text-[#384860]">{t.errorLink}</h1>
                )}

                {view === 'confirm' && (
                    <>
                        <h1 className="text-2xl font-semibold text-[#384860]">{t.title}</h1>
                        <p className="mt-3 mb-6 text-[#384860]/70">{t.body}</p>
                        <button
                            onClick={doUnsubscribe}
                            disabled={busy}
                            className="w-full rounded-xl bg-[#384860] hover:bg-[#2c3a4e] disabled:opacity-50 text-white font-semibold py-3 transition-colors"
                        >
                            {busy ? t.working : t.confirm}
                        </button>
                    </>
                )}

                {(view === 'done' || view === 'already') && (
                    <>
                        <div className="mx-auto h-16 w-16 rounded-2xl bg-[#93cbb6]/20 flex items-center justify-center mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2f7d5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-semibold text-[#384860]">{view === 'done' ? t.doneTitle : t.alreadyTitle}</h1>
                        <p className="mt-3 text-[#384860]/70">{t.doneBody}</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default RecallUnsubscribePage;
